/**
 * Константы для работы с хранилищем (localStorage, sessionStorage).
 * Часть экспортов (STORAGE_DEFAULTS, STORAGE_LIMITS, PREFIXES, TIMESTAMP_* и др.) зарезервирована для единообразия и будущего использования.
 */

/**
 * Ключи для localStorage
 */
export const LOCAL_STORAGE_KEYS = {
  /** Токен аутентификации */
  TOKEN: "token",
  /** Refresh токен */
  REFRESH_TOKEN: "refreshToken",
  /** Временная метка токена */
  TOKEN_TIMESTAMP: "token_timestamp",
  /** Dev мок токен */
  DEV_MOCK_TOKEN: "dev-mock-token",
  /** Кэшированные данные пользователя */
  CACHED_USER: "cached-user",
  /** Состояние авторизации (Zustand persist) */
  AUTH_STORAGE: "auth-storage",
  /** Логи приложения */
  APP_LOGS: "goodluck_logs",
  /** ID пользователя */
  USER_ID: "userId",
  /** Согласие на cookies */
  COOKIE_CONSENT: "cookie-consent",
  /** Режим просмотра турниров */
  TOURNAMENTS_VIEW_MODE: "tournaments_view_mode",
  /** Настройки пользователя */
  USER_SETTINGS: "user-settings",
  /** Настройки уведомлений */
  NOTIFICATION_SETTINGS: "notification-settings",
  /** История поиска */
  SEARCH_HISTORY: "search-history",
  /** Избранные турниры */
  FAVORITE_TOURNAMENTS: "favorite-tournaments",
  /** Избранные команды */
  FAVORITE_TEAMS: "favorite-teams",
  /** Избранные организации */
  FAVORITE_ORGANIZATIONS: "favorite-organizations",
  /** Настройки темы */
  THEME_SETTINGS: "theme-settings",
  /** Языковые настройки */
  LANGUAGE_SETTINGS: "language-settings",
  /** Настройки анимаций */
  ANIMATION_SETTINGS: "animation-settings",
  /** Настройки звука */
  SOUND_SETTINGS: "sound-settings",
  /** Настройки уведомлений браузера */
  BROWSER_NOTIFICATION_SETTINGS: "browser-notification-settings",
  /** Язык интерфейса */
  LANGUAGE: "language",
  /** Оверлей включён */
  OVERLAY_ENABLED: "overlay-enabled",
  /** Смещение оверлея X */
  OVERLAY_OFFSET_X: "overlay-offset-x",
  /** Смещение оверлея Y */
  OVERLAY_OFFSET_Y: "overlay-offset-y",
  /** Позиции ников на экране загрузки (центр-относительные) [[x,y], ...] */
  OVERLAY_LOADING_POSITIONS: "overlay-loading-positions",
  /** Режим отладки оверлея (dev only) */
  OVERLAY_DEBUG: "overlay-debug-enabled",
  /** Настройки превью профиля (что показывать при hover) */
  PROFILE_PREVIEW_SETTINGS: "profile-preview-settings",
  /** Последний имперсонированный пользователь (admin) */
  IMPERSONATE_LAST: "admin-impersonate-last",
  /** Админ: оверрайды design tokens для тестирования UI/UX */
  ADMIN_UI_OVERRIDES: "admin-ui-overrides",
  /** Админ: экспериментальная тема нео-фольклор (только UI/UX панель) */
  ADMIN_NEO_THEME: "admin-neo-theme",
  /** Режим отображения списка организаций (grid/list) */
  ORGANIZATIONS_VIEW_MODE: "organizations-view-mode",
  /** Расположение хедера (top/bottom/left/right) */
  HEADER_LAYOUT: "header-layout",
} as const;

/**
 * Ключи для sessionStorage
 */
export const SESSION_STORAGE_KEYS = {
  /** ID сессии */
  SESSION_ID: "goodluck_session_id",
  /** Временные данные формы */
  FORM_DATA: "form-data",
  /** Временные настройки фильтров */
  FILTER_SETTINGS: "filter-settings",
  /** Временные данные пагинации */
  PAGINATION_DATA: "pagination-data",
  /** Временные данные сортировки */
  SORT_DATA: "sort-data",
  /** Временные данные модальных окон */
  MODAL_DATA: "modal-data",
  /** Временные данные уведомлений */
  NOTIFICATION_DATA: "notification-data",
  /** Временные данные навигации */
  NAVIGATION_DATA: "navigation-data",
  /** ID сессии GoodLuck */
  GOODLUCK_SESSION_ID: "goodluck_session_id",
  /** Подсказка «был залогинен» для мгновенного UI (userId + ts, TTL ~5 min) */
  AUTH_HINT: "gl_auth_hint",
} as const;

/**
 * Ограничения для хранилища
 */
export const STORAGE_LIMITS = {
  /** Максимальное количество логов в localStorage */
  MAX_LOGS_COUNT: 100,
  /** Максимальная длина ключа */
  MAX_KEY_LENGTH: 100,
  /** Максимальная длина значения */
  MAX_VALUE_LENGTH: 10000,
  /** Максимальное количество элементов в массиве */
  MAX_ARRAY_LENGTH: 1000,
  /** Максимальная глубина объекта */
  MAX_OBJECT_DEPTH: 10,
  /** Максимальный размер данных в байтах */
  MAX_DATA_SIZE: 5 * 1024 * 1024, // 5MB
} as const;

/**
 * Валидирует только критически важные константы хранилища
 */
function validateCriticalStorageConstants(): void {
  // Валидация максимального размера данных (критично для производительности)
  if (STORAGE_LIMITS.MAX_DATA_SIZE > 10 * 1024 * 1024) {
    throw new Error("MAX_DATA_SIZE не должен превышать 10MB");
  }

  // Валидация максимального количества логов (критично для памяти)
  if (STORAGE_LIMITS.MAX_LOGS_COUNT > 1000) {
    throw new Error("MAX_LOGS_COUNT не должен превышать 1000");
  }
}

// Запускаем валидацию только критических констант
validateCriticalStorageConstants();
